import { createContext, useContext, useEffect, useState } from 'react';
import storageManager from '../utils/StorageManager';

const themes = {
  dark: {
    primary: '#222831',
    secondary: '#393E46',
    tertiary: '#4A505A',
    light: '#EEEEEE',

    bgDark: '#222831',
    bgMedium: '#393E46',
    bgLight: '#4A505A',

    textPrimary: '#EEEEEE',
    textSecondary: '#C8CDD3',
    textDisabled: '#8D949E',

    accent: '#D65A31',
    accentHover: '#E17756',

    success: '#D65A31',
    danger: '#BF4527',
    warning: '#C87A3B',

    cardColor1: '#393E46',
    cardColor2: '#D65A31'
  },
  
  light: {
    primary: '#EEEEEE',
    secondary: '#393E46',
    tertiary: '#222831',
    light: '#222831',

    bgDark: '#FFFFFF',
    bgMedium: '#EEEEEE',
    bgLight: '#D9DDE2',

    textPrimary: '#222831',
    textSecondary: '#393E46',
    textDisabled: '#80858D',

    accent: '#D65A31',
    accentHover: '#B94D2A',

    success: '#D65A31',
    danger: '#B24729',
    warning: '#A96631',

    cardColor1: '#D7DADF',
    cardColor2: '#F3D7CE'
  }
};

const ThemeContext = createContext();

const hexToRgbTriplet = (hexValue, fallback = '57 62 70') => {
  if (!hexValue || typeof hexValue !== 'string') return fallback;
  const hex = hexValue.replace('#', '').trim();
  if (![3, 6].includes(hex.length)) return fallback;

  const normalized = hex.length === 3
    ? hex.split('').map((char) => `${char}${char}`).join('')
    : hex;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  if ([r, g, b].some((value) => Number.isNaN(value))) return fallback;
  return `${r} ${g} ${b}`;
};

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = storageManager.getTheme();
    return themes[savedTheme] ? savedTheme : 'dark';
  });
  
  const theme = themes[currentTheme];

  useEffect(() => {
    storageManager.saveTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--gn-bg-medium-rgb', hexToRgbTriplet(theme.bgMedium));
    root.style.setProperty('--gn-bg-dark-rgb', hexToRgbTriplet(theme.bgDark));
    root.style.setProperty('--gn-secondary-rgb', hexToRgbTriplet(theme.secondary));
    root.style.setProperty('--gn-accent-rgb', hexToRgbTriplet(theme.accent));
    root.style.setProperty('--gn-light-rgb', hexToRgbTriplet(theme.light || theme.textPrimary, '238 238 238'));
    root.style.setProperty('--gn-text-primary-rgb', hexToRgbTriplet(theme.textPrimary, '238 238 238'));
    root.style.setProperty('--gn-texture-strength', currentTheme === 'dark' ? '0.34' : '0.18');
    root.style.setProperty('--gn-dot-strength', currentTheme === 'dark' ? '0.22' : '0.08');
    root.style.setProperty('--gn-overlay-blend', currentTheme === 'dark' ? 'soft-light' : 'multiply');
    root.style.setProperty('--gn-dot-blend', currentTheme === 'dark' ? 'overlay' : 'normal');
    root.style.setProperty(
      '--gn-pattern-image',
      currentTheme === 'dark'
        ? "url('/patterns/vecteezy-gaming-pattern-dark.jpg')"
        : "url('/patterns/vecteezy-gaming-pattern-light.jpg')"
    );
    root.style.setProperty('--gn-pattern-size', currentTheme === 'dark' ? '500px' : '480px');
    root.style.setProperty('--gn-page-overlay-alpha', currentTheme === 'dark' ? '0.70' : '0.52');
    root.style.setProperty('--gn-pattern-tint-rgb', currentTheme === 'dark' ? hexToRgbTriplet(theme.accent) : hexToRgbTriplet(theme.secondary));
    root.style.setProperty('--gn-pattern-tint-alpha', currentTheme === 'dark' ? '0.11' : '0.09');
    root.style.setProperty('--gn-top-pattern-opacity', '0.03');
    root.style.setProperty('--gn-top-pattern-blend', 'normal');
    root.dataset.theme = currentTheme;
    root.style.colorScheme = currentTheme;
  }, [theme, currentTheme]);

  const setTheme = (nextTheme) => {
    setCurrentTheme(themes[nextTheme] ? nextTheme : 'dark');
  };
  
  const toggleTheme = () => {
    setTheme(currentTheme === 'dark' ? 'light' : 'dark');
  };
  
  return (
    <ThemeContext.Provider value={{ theme, currentTheme, setTheme, toggleTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
